import React, { useState, useEffect, useMemo } from "react";
import { useAuth } from "../context/AuthContext";
import YouTubeAnalyticsPanel from "./YouTubeAnalyticsPanel";
import MetadataOptimizer from "./youtube/MetadataOptimizer";
import CompetitorAnalysis from "./youtube/CompetitorAnalysis";
import ChannelHealth from "./youtube/ChannelHealth";
import QuickEdit from "./youtube/QuickEdit";
import AuditHistory from "./youtube/AuditHistory";
import TrendChart from "./youtube/TrendChart";
import PerformanceHub from "./youtube/PerformanceHub";
import AdvancedMetadataEditor from "./youtube/AdvancedMetadataEditor";
import { fetchChannelVideos, YouTubeVideo } from "../services/youtubeAdvanced";
import { dummyChannels, YouTubeChannel, getChannelTrends } from "../services/multiChannelService";
import { dummyChannelVideos } from "../services/dummyData";
import { BarChart2, Edit3, Activity, Settings2 } from "lucide-react";

const dashboardContainerStyle: React.CSSProperties = {
  padding: "40px 5%",
  background: "#f8fafc",
  minHeight: "100vh",
  fontFamily: "'Inter', sans-serif",
};

const cardStyle: React.CSSProperties = {
  background: "#fff",
  padding: "24px",
  borderRadius: "16px",
  boxShadow: "0 4px 6px -1px rgb(0 0 0 / 0.1)",
  border: "1px solid #f1f5f9"
};

const YouTubeWorldClassDashboard: React.FC = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState<"overview" | "pulse" | "studio" | "competitors" | "strategy">("overview");
  const [contentType, setContentType] = useState<"all" | "video" | "shorts" | "polls">("all");
  const [currentChannel, setCurrentChannel] = useState<YouTubeChannel>(dummyChannels[0]);
  const [videos, setVideos] = useState<YouTubeVideo[]>([]);
  const [loading, setLoading] = useState(false);
  const [editingVideo, setEditingVideo] = useState<YouTubeVideo | null>(null);
  const [analyticsVideo, setAnalyticsVideo] = useState<YouTubeVideo | null>(null);

  const GOOGLE_API_KEY = "YOUR_GOOGLE_API_KEY"; 
  const GROQ_API_KEY = "YOUR_GROQ_API_KEY"; 

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      const vids = await fetchChannelVideos(GOOGLE_API_KEY, currentChannel.id);
      setVideos(vids);
      setLoading(false);
    };
    loadData();
  }, [currentChannel]);

  const trends = useMemo(() => getChannelTrends(currentChannel.id), [currentChannel.id]);

  if (!user) {
    return (
      <div style={dashboardContainerStyle}>
        <div style={{ textAlign: "center", paddingTop: "32px" }}>
          <h2>Please sign in to access your Command Center.</h2>
        </div>
      </div>
    );
  }

  return (
    <div style={dashboardContainerStyle}>
      <header style={{ marginBottom: "40px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div>
          <h1 style={{ fontSize: "28px", fontWeight: 800, color: "#0f172a", margin: 0 }}>
            Creator Studio <span style={{ color: "#6366f1" }}>.</span>
          </h1>
          <p style={{ color: "#64748b", margin: "4px 0 0" }}>Managing {dummyChannels.length} connected channels</p>
        </div>

        <div style={{ display: "flex", gap: "16px", alignItems: "center" }}>
          <div style={{ textAlign: "right" }}>
             <p style={{ margin: 0, fontSize: "11px", fontWeight: 700, color: "#94a3b8", textTransform: "uppercase" }}>Channel Filter</p>
             <select 
                value={currentChannel.id}
                onChange={(e) => setCurrentChannel(dummyChannels.find(c => c.id === e.target.value)!)}
                style={{ background: "#fff", border: "1px solid #e2e8f0", padding: "8px 12px", borderRadius: "8px", fontWeight: 600, color: "#1e293b", cursor: "pointer" }}
             >
                {dummyChannels.map(c => <option key={c.id} value={c.id}>{c.title}</option>)}
             </select>
          </div>
          <img src={currentChannel.thumbnail} style={{ width: "40px", height: "40px", borderRadius: "10px", objectFit: "cover", border: "2px solid #fff", boxShadow: "0 4px 10px rgba(0,0,0,0.1)" }} alt="" />
        </div>
      </header>

      {/* DASHNAV */}
      <nav style={{ display: "flex", gap: "32px", borderBottom: "1px solid #e2e8f0", marginBottom: "32px" }}>
        {["overview", "studio", "competitors", "strategy"].map(t => (
            <div 
                key={t}
                onClick={() => setActiveTab(t as any)}
                style={{
                    paddingBottom: "12px",
                    borderBottom: activeTab === t ? "2px solid #6366f1" : "2px solid transparent",
                    color: activeTab === t ? "#6366f1" : "#64748b",
                    fontWeight: 600,
                    cursor: "pointer",
                    textTransform: "capitalize",
                    transition: "all 0.2s"
                }}
            >
                {t === "studio" ? "Studio Intelligence" : t}
            </div>
        ))}
      </nav>

      {/* OVERVIEW CONTENT */}
      {activeTab === "overview" && (
        <div style={{ animation: "fadeIn 0.4s ease" }}>
          {/* AGGREGATED STATS & TRENDS */}
          <div style={{ display: "flex", gap: "20px", marginBottom: "32px" }}>
              <div style={{ ...cardStyle, flex: 1.2, display: "flex", flexWrap: "wrap", justifyContent: "space-between", background: "linear-gradient(135deg, #1e293b 0%, #0f172a 100%)", color: "#fff" }}>
                 <div style={{ minWidth: "120px" }}>
                    <p style={{ margin: "0 0 4px 0", opacity: 0.6, fontSize: "11px", fontWeight: 700 }}>SUBS</p>
                    <h2 style={{ margin: 0, fontSize: "24px" }}>{currentChannel.stats.subscriberCount.toLocaleString()}</h2>
                 </div>
                 <div style={{ minWidth: "120px" }}>
                    <p style={{ margin: "0 0 4px 0", opacity: 0.6, fontSize: "11px", fontWeight: 700 }}>LIKES</p>
                    <h2 style={{ margin: 0, fontSize: "24px" }}>{currentChannel.stats.totalLikes.toLocaleString()}</h2>
                 </div>
                 <div style={{ width: "100%", height: "1px", background: "rgba(255,255,255,0.1)", margin: "16px 0" }}></div>
                 <div style={{ flex: 1, display: "flex", justifyContent: "space-between" }}>
                    <div>
                        <p style={{ margin: 0, opacity: 0.6, fontSize: "10px", fontWeight: 700 }}>VIDEOS</p>
                        <p style={{ margin: 0, fontSize: "16px", fontWeight: 700 }}>{currentChannel.stats.totalVideos}</p>
                    </div>
                    <div>
                        <p style={{ margin: 0, opacity: 0.6, fontSize: "10px", fontWeight: 700 }}>SHORTS</p>
                        <p style={{ margin: 0, fontSize: "16px", fontWeight: 700 }}>{currentChannel.stats.totalShorts}</p>
                    </div>
                    <div>
                        <p style={{ margin: 0, opacity: 0.6, fontSize: "10px", fontWeight: 700 }}>POLLS</p>
                        <p style={{ margin: 0, fontSize: "16px", fontWeight: 700 }}>{currentChannel.stats.totalPolls}</p>
                    </div>
                    <div>
                        <p style={{ margin: 0, opacity: 0.6, fontSize: "10px", fontWeight: 700 }}>COMMENTS</p>
                        <p style={{ margin: 0, fontSize: "16px", fontWeight: 700 }}>{currentChannel.stats.totalComments.toLocaleString()}</p>
                    </div>
                 </div>
              </div>
              <TrendChart data={trends.map(t => t.subscribers)} color="#6366f1" label="Sub Growth" />
              <TrendChart data={trends.map(t => t.likes)} color="#10b981" label="Likes Trend" />
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 340px", gap: "32px" }}>
             <div style={cardStyle}>
                <h3 style={{ marginTop: 0, marginBottom: "20px" }}>Content Management</h3>
                <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                   {videos.map(v => (
                       <div key={v.id} style={{ display: "flex", gap: "16px", padding: "12px", background: "#f8fafc", borderRadius: "12px", border: "1px solid #f1f5f9", alignItems: "center" }}>
                          <img src={v.thumbnail} style={{ width: "80px", borderRadius: "8px" }} alt="" />
                          <div style={{ flex: 1 }}>
                             <p style={{ margin: 0, fontWeight: 700, fontSize: "14px", color: "#1e293b" }}>{v.title}</p>
                             <div style={{ display: "flex", gap: "12px", marginTop: "4px" }}>
                                <span style={{ fontSize: "11px", color: "#64748b" }}>📅 {new Date(v.publishedAt).toLocaleDateString()}</span>
                                <span style={{ fontSize: "11px", color: "#10b981", fontWeight: 700 }}>4.8% CTR</span>
                             </div>
                          </div>
                          <div style={{ display: "flex", gap: "8px" }}>
                            <button 
                                onClick={() => setEditingVideo(v)}
                                title="Advanced Metadata Staging"
                                style={{ padding: "8px", borderRadius: "8px", border: "1px solid #e2e8f0", background: "#fff", cursor: "pointer", color: "#6366f1" }}
                            ><Edit3 size={16}/></button>
                            <button 
                                onClick={() => { setAnalyticsVideo(v); setActiveTab("studio"); }}
                                title="Studio Insights"
                                style={{ padding: "8px", borderRadius: "8px", border: "1px solid #e2e8f0", background: "#fff", cursor: "pointer", color: "#10b981" }}
                            ><Activity size={16}/></button>
                          </div>
                       </div>
                   ))}
                </div>
             </div>
             <ChannelHealth videos={videos} />
          </div>
        </div>
      )}

      {/* STUDIO INTELLIGENCE (Analytics Hub) */}
      {activeTab === "studio" && (
        <PerformanceHub 
            apiKey={GOOGLE_API_KEY} 
            videoId={analyticsVideo?.id}
            videoTitle={analyticsVideo?.title}
        />
      )}

      {/* STRATEGY (Optimization Tools) */}
      {activeTab === "strategy" && (
        <div style={{ animation: "fadeIn 0.3s ease" }}>
          <MetadataOptimizer groqApiKey={GROQ_API_KEY} currentTitle={videos[0]?.title || ""} currentDescription={videos[0]?.description || ""} niche="General" />
          <div style={{ marginTop: "32px" }}>
             <AuditHistory />
          </div>
        </div>
      )}

      {/* COMPETITORS */}
      {activeTab === "competitors" && (
        <CompetitorAnalysis apiKey={GOOGLE_API_KEY} />
      )}

      {/* GLOBAL PULSE (Timeline) */}
      {activeTab === "pulse" && (
        <div style={{ animation: "fadeIn 0.3s ease", maxWidth: "800px" }}>
            <h3 style={{ marginBottom: "20px" }}>Global Pulse Timeline</h3>
            <p style={{ color: "#64748b", marginBottom: "32px" }}>Unified publishing history across all channels.</p>
            {/* ... simplified timeline view ... */}
            <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
                {dummyChannelVideos.map((v, i) => (
                    <div key={i} style={{ ...cardStyle, borderLeft: "4px solid #6366f1" }}>
                        <span style={{ fontSize: "11px", fontWeight: 800, color: "#6366f1" }}>CH: CODE MASTER</span>
                        <p style={{ margin: "4px 0", fontWeight: 700 }}>{v.title}</p>
                        <span style={{ fontSize: "11px", color: "#94a3b8" }}>Published {new Date(v.publishedAt).toLocaleDateString()}</span>
                    </div>
                ))}
            </div>
        </div>
      )}

      {/* MODALS */}
      {editingVideo && (
        <AdvancedMetadataEditor 
            video={editingVideo} 
            apiKey={GOOGLE_API_KEY}
            onClose={() => setEditingVideo(null)}
            onPushed={() => {
                setEditingVideo(null);
                // Trigger refresh if needed
            }}
        />
      )}

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
};

export default YouTubeWorldClassDashboard;
